import { api } from "@/lib/apiClient";
import { updateDokument, type DokumentDetail, type DokumentTyp } from "@/lib/dokumente";

export type GdStandardtextBereich = "einleitung" | "schlusstext";

export interface GdStandardtext {
  dokument_typ: DokumentTyp;
  bereich: GdStandardtextBereich;
  inhalt: string;
  updated_by?: string | null;
  updated_at?: string | null;
}

export interface GdStandardtexteMap {
  einleitung: string;
  schlusstext: string;
}

export function listGdStandardtexte(typ?: DokumentTyp): Promise<GdStandardtext[]> {
  const params = new URLSearchParams();
  if (typ) params.set("typ", typ);
  const query = params.toString();
  return api.get<GdStandardtext[]>(`/dokumente/standardtexte${query ? `?${query}` : ""}`);
}

export function mapGdStandardtexte(rows: GdStandardtext[]): GdStandardtexteMap {
  return {
    einleitung: rows.find((row) => row.bereich === "einleitung")?.inhalt?.trim() || "",
    schlusstext: rows.find((row) => row.bereich === "schlusstext")?.inhalt?.trim() || "",
  };
}

export async function loadGdStandardtexteMap(typ: DokumentTyp): Promise<GdStandardtexteMap> {
  try {
    return mapGdStandardtexte(await listGdStandardtexte(typ));
  } catch {
    return { einleitung: "", schlusstext: "" };
  }
}

/** Füllt fehlende Einleitung/Schluss aus den Standardtexten des Dokumenttyps. */
export async function ensureDokumentStandardtexte(doc: DokumentDetail): Promise<DokumentDetail> {
  const missingEinleitung = !doc.einleitung?.trim();
  const missingSchluss = !doc.schlusstext?.trim();
  if (!missingEinleitung && !missingSchluss) return doc;
  const typ = doc.dokument_typ;
  if (typ !== "angebot" && typ !== "rechnung" && typ !== "lieferschein" && typ !== "auftragsbestaetigung") {
    return doc;
  }
  const texts = await loadGdStandardtexteMap(typ);
  const einleitung = missingEinleitung ? texts.einleitung : undefined;
  const schlusstext = missingSchluss ? texts.schlusstext : undefined;
  if (!einleitung && !schlusstext) return doc;
  return updateDokument(doc.id, {
    version: doc.version,
    ...(einleitung ? { einleitung } : {}),
    ...(schlusstext ? { schlusstext } : {}),
  });
}

export function saveGdStandardtext(
  typ: DokumentTyp,
  bereich: GdStandardtextBereich,
  inhalt: string,
): Promise<GdStandardtext> {
  return api.put<GdStandardtext>(`/dokumente/standardtexte/${typ}/${bereich}`, { inhalt });
}

export function deleteGdStandardtext(
  typ: DokumentTyp,
  bereich: GdStandardtextBereich,
): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/dokumente/standardtexte/${typ}/${bereich}`);
}
